import React from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, ArrowRightIcon, EyeIcon } from 'lucide-react';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
interface Account {
  id: string;
  bankName: string;
  bankLogo: string;
  accountType: string;
  accountNumber: string;
  balance: number;
  currency: string;
}
interface AccountsScreenProps {
  accounts: Account[];
  onAddAccount: () => void;
  onViewAccount: (accountId: string) => void;
}
export function AccountsScreen({
  accounts,
  onAddAccount,
  onViewAccount
}: AccountsScreenProps) {
  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);
  return <div className="space-y-6" data-id="element-194">
      {/* Total Balance Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gradient-to-br rounded-2xl p-6 text-white shadow-lg" style={{
      background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.primary600} 100%)`
    }} data-id="element-195">
        <div className="text-sm font-medium opacity-80 uppercase tracking-wide mb-2" data-id="element-196">
          Total Across All Accounts
        </div>
        <div className="text-4xl font-bold mb-1" data-id="element-197">
          AED {totalBalance.toLocaleString()}
        </div>
        <div className="text-sm opacity-80" data-id="element-198">
          {accounts.length} connected account{accounts.length !== 1 ? 's' : ''}
        </div>
      </motion.div>

      {/* Add Account CTA */}
      <motion.button initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} onClick={onAddAccount} className="w-full bg-white border-2 border-dashed rounded-2xl p-6 flex items-center justify-center gap-3 transition-all hover:border-solid hover:shadow-md" style={{
      borderColor: OPENFINANCE_THEME.colors.primary500 + '40'
    }} data-id="element-199">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary500 + '15'
      }} data-id="element-200">
          <PlusIcon className="w-5 h-5" style={{
          color: OPENFINANCE_THEME.colors.primary500
        }} data-id="element-201" />
        </div>
        <div className="text-left" data-id="element-202">
          <div className="text-base font-bold" style={{
          color: OPENFINANCE_THEME.colors.text,
          fontFamily: OPENFINANCE_THEME.fonts.heading
        }} data-id="element-203">
            Add Bank Account
          </div>
          <div className="text-sm" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-204">
            Connect another bank to VeloX
          </div>
        </div>
      </motion.button>

      {/* Connected Accounts */}
      <div data-id="element-205">
        <h2 className="text-lg font-bold mb-4" style={{
        color: OPENFINANCE_THEME.colors.text,
        fontFamily: OPENFINANCE_THEME.fonts.heading
      }} data-id="element-206">
          Connected Accounts
        </h2>

        <div className="space-y-3" data-id="element-207">
          {accounts.map((account, index) => <motion.button key={account.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.1
        }} onClick={() => onViewAccount(account.id)} className="w-full bg-white border-2 rounded-2xl p-5 text-left transition-all hover:shadow-md hover:-translate-y-1" style={{
          borderColor: OPENFINANCE_THEME.colors.border
        }} data-id="element-208">
              <div className="flex items-center gap-4" data-id="element-209">
                {/* Bank Logo */}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center p-2 flex-shrink-0" style={{
              backgroundColor: OPENFINANCE_THEME.colors.borderLight
            }} data-id="element-210">
                  <img src={account.bankLogo} alt={account.bankName} className="w-full h-full object-contain" data-id="element-211" />
                </div>

                {/* Account Info */}
                <div className="flex-1 min-w-0" data-id="element-212">
                  <div className="text-base font-bold mb-1" style={{
                color: OPENFINANCE_THEME.colors.text,
                fontFamily: OPENFINANCE_THEME.fonts.heading
              }} data-id="element-213">
                    {account.bankName}
                  </div>
                  <div className="text-sm mb-1" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-214">
                    {account.accountType} • ****
                    {account.accountNumber.slice(-4)}
                  </div>
                  <div className="text-lg font-bold" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-215">
                    {account.currency} {account.balance.toLocaleString()}
                  </div>
                </div>

                {/* Arrow */}
                <ArrowRightIcon className="w-5 h-5 flex-shrink-0" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-216" />
              </div>
            </motion.button>)}
        </div>
      </div>
    </div>;
}